import React from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import Seo from "./components/Seo.jsx";

// AppShell receives a `screen` prop and renders the right surface. We keep
// the screen-name plumbing instead of mounting a different component per
// route because the existing App component owns a lot of cross-screen
// state (active profile, opportunities cache, scan dialog, toasts, etc).
// react-router-dom owns the URL; App owns what to render given a screen.
const SCREEN_BY_PATH = {
  "/": "dashboard",
  "/dashboard": "dashboard",
  "/opportunities": "opportunities",
  "/conversations": "conversations",
  "/scans": "scans",
  "/profile": "profile",
  "/settings": "settings",
  "/pricing": "pricing",
  "/mobile": "mobile",
};

export const PATH_BY_SCREEN = {
  dashboard: "/dashboard",
  opportunities: "/opportunities",
  conversations: "/conversations",
  scans: "/scans",
  profile: "/profile",
  settings: "/settings",
  pricing: "/pricing",
  mobile: "/mobile",
};

export const AUTHED_PATHS = Object.values(PATH_BY_SCREEN);

export const LEGAL_PATHS = ["/terms", "/privacy-policy", "/privacy", "/contact", "/support"];

export function screenForPath(pathname) {
  if (/^\/scan\/[^/]+$/i.test(pathname || "")) return "scans";
  if (/^\/opportunity\/[^/]+$/i.test(pathname || "")) return "opportunities";
  return SCREEN_BY_PATH[pathname] || null;
}

export function isAuthedPath(pathname) {
  if (/^\/scan\/[^/]+$/i.test(pathname || "")) return true;
  if (/^\/opportunity\/[^/]+$/i.test(pathname || "")) return true;
  return AUTHED_PATHS.includes(pathname);
}

export function isLegalPath(pathname) {
  return LEGAL_PATHS.includes(pathname);
}

export function scanIdForPath(pathname) {
  const match = String(pathname || "").match(/^\/scan\/([^/]+)$/i);
  return match ? decodeURIComponent(match[1]) : null;
}

export function scanPath(scanId) {
  return `/scan/${encodeURIComponent(scanId)}`;
}

export function opportunityIdForPath(pathname) {
  const match = String(pathname || "").match(/^\/opportunity\/([^/]+)$/i);
  return match ? decodeURIComponent(match[1]) : null;
}

export function opportunityPath(opportunityId) {
  return `/opportunity/${encodeURIComponent(opportunityId)}`;
}

export function pathMatchesScreen(screen, pathname, isAuthed = false) {
  if (screen === "scans") {
    return pathname === "/scans" || /^\/scan\/[^/]+$/i.test(pathname || "");
  }
  if (screen === "opportunities") {
    return pathname === "/opportunities" || /^\/opportunity\/[^/]+$/i.test(pathname || "");
  }
  if (screen === "dashboard") {
    return pathname === (isAuthed ? "/dashboard" : "/");
  }
  return pathname === (PATH_BY_SCREEN[screen] || "/");
}

// Public legal pages live outside the AppShell so they render without the
// sidebar/topbar and don't require auth or app state. They register on
// window.TermsScreen / window.PrivacyScreen via screens/legal.jsx; we read
// them at render time so this module doesn't need a static import.
function LegalRoute({ which }) {
  const Comp =
    which === "terms"
      ? window.TermsScreen
      : which === "privacy"
        ? window.PrivacyScreen
        : which === "blogs"
          ? window.BlogsScreen
          : which === "blogPost"
            ? window.BlogPostScreen
            : which === "alternatives"
              ? window.AlternativesScreen
              : which === "alternativePage"
                ? window.AlternativePageScreen
                : which === "why"
              ? window.WhyItWorksScreen
              : which === "how"
                ? window.HowItWorksScreen
                : which === "airspaceLive"
                  ? window.AirSpaceLiveScreen
                  : which === "airspaceLivePrivacy"
                    ? window.AirSpaceLivePrivacyScreen
                    : window.ContactScreen;
  if (!Comp) {
    return (
      <main style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 32, fontFamily: "Inter, system-ui, sans-serif" }}>
        <p style={{ color: "var(--text-secondary)" }}>Loading…</p>
      </main>
    );
  }
  return <Comp />;
}

// 404 page. Plain markup so even unbundled crawlers see something.
function NotFound() {
  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 32,
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      <Seo title="Page not found" description="The page you tried to reach doesn't exist." noindex />
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <h1 style={{ fontSize: 48, margin: 0, color: "var(--text-primary)" }}>404</h1>
        <p style={{ color: "var(--text-secondary)", marginTop: 12 }}>
          We couldn't find that page.
        </p>
        <a href="/" style={{ color: "var(--accent)", marginTop: 16, display: "inline-block" }}>
          Go home →
        </a>
      </div>
    </main>
  );
}

/**
 * Define the real routes. All routes that map to an in-app surface mount
 * the same <AppShellComponent /> — App.jsx reads the URL itself via
 * useLocation and renders the appropriate screen. This keeps cross-screen
 * state alive across navigations (no remount-on-route-change) while still
 * giving us crawlable, shareable, refresh-safe URLs.
 */
export default function AppRouter({ shell: AppShellComponent }) {
  return (
    <Routes>
      <Route path="/" element={<AppShellComponent />} />
      <Route path="/login" element={<AppShellComponent />} />
      <Route path="/onboarding" element={<AppShellComponent />} />
      <Route path="/dashboard" element={<AppShellComponent />} />
      <Route path="/opportunities" element={<AppShellComponent />} />
      <Route path="/conversations" element={<AppShellComponent />} />
      <Route path="/opportunity/:opportunityId" element={<AppShellComponent />} />
      <Route path="/scans" element={<AppShellComponent />} />
      <Route path="/scan/:scanId" element={<AppShellComponent />} />
      <Route path="/profile" element={<AppShellComponent />} />
      <Route path="/settings" element={<AppShellComponent />} />
      <Route path="/pricing" element={<AppShellComponent />} />
      <Route path="/mobile" element={<AppShellComponent />} />
      <Route path="/why-it-works" element={<LegalRoute which="why" />} />
      <Route path="/how-it-works" element={<LegalRoute which="how" />} />
      <Route path="/airspace-live-app" element={<LegalRoute which="airspaceLive" />} />
      <Route path="/airspace-live-app-privacy" element={<LegalRoute which="airspaceLivePrivacy" />} />
      <Route path="/blogs" element={<LegalRoute which="blogs" />} />
      <Route path="/blogs/:slug" element={<LegalRoute which="blogPost" />} />
      <Route path="/alternatives" element={<LegalRoute which="alternatives" />} />
      <Route path="/alternatives/:slug" element={<LegalRoute which="alternativePage" />} />
      <Route path="/terms" element={<LegalRoute which="terms" />} />
      <Route path="/privacy-policy" element={<LegalRoute which="privacy" />} />
      <Route path="/privacy" element={<LegalRoute which="privacy" />} />
      <Route path="/contact" element={<LegalRoute which="contact" />} />
      <Route path="/support" element={<LegalRoute which="contact" />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// Helper hook: read the current URL pathname, normalised. Used by App to
// decide whether to show the landing, onboarding, or an authed screen.
export function useCurrentPath() {
  return useLocation().pathname;
}

export { Navigate };
